import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import logo from '../logo.png';
import { buildPartsItemsFromTicket } from './ticketPartsUtils';

const API = '/api';

const STATUSES = [
  { value: 'received',      label: 'Παραλήφθηκε',    short: 'Παραλήφθηκε' },
  { value: 'diagnosing',    label: 'Διάγνωση',        short: 'Διάγνωση'    },
  { value: 'in_repair',     label: 'Σε Επισκευή',     short: 'Επισκευή'    },
  { value: 'waiting_parts', label: 'Αναμονή Εξαρτ.',  short: 'Αναμονή'     },
  { value: 'ready',         label: 'Έτοιμο',           short: 'Έτοιμο'      },
  { value: 'delivered',     label: 'Παραδόθηκε',      short: 'Παραδόθηκε'  },
  { value: 'credited',      label: 'Πιστωτικό',       short: 'Πιστωτικό'   },  { value: 'credited_deleted', label: 'Πιστωτικό (Αρχείο)', short: 'Πιστωτικό' },  { value: 'irreparable',   label: 'Ανεπισκεύαστο',   short: 'Ανεπ/στο'    },
];

const WORK_TYPES = [
  '', 'ΕΠΙΣΚΕΥΗ ΣΥΣΚΕΥΗΣ', 'ΑΝΑΒΑΘΜΙΣΗ ΣΥΣΚΕΥΗΣ', 'ΞΕΚΛΕΙΔΩΜΑ ΣΥΣΚΕΥΗΣ',
  'ΜΕΤΑΦΟΡΑ ΔΕΔΟΜΕΝΩΝ', 'ΕΛΕΓΧΟΣ ΣΥΣΚΕΥΗΣ', 'ΔΗΜΙΟΥΡΓΙΑ ΛΟΓΑΡΙΑΣΜΟΥ CLOUD',
];

const STATUS_IDX = Object.fromEntries(STATUSES.map((s, i) => [s.value, i]));

function emptyPart() { return { id: Date.now() + Math.random(), part_code: '', part_name: '', parts_price: '' }; }

const EMPTY_FORM = {
  customer_name: '', customer_phone: '', customer_email: '', imei: '',
  device_model: '', accessories: '', work_type: '', notes: '',
  labor_price: '', total_price: '',
  status: 'received',
  repair_notes: '',
  quantity_receipt: false,
  partsItems: [emptyPart()],
};

export default function Tickets() {
  const { isAdmin, isCashier, isTech } = useAuth();
  const printRef = useRef();

  const MONTH_NAMES = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαι', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [filterMode, setFilterMode] = useState('month');

  const [tickets,       setTickets]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [filterSt,      setFilterSt]      = useState('');
  const [msg,           setMsg]           = useState(null);
  const [showModal,     setShowModal]     = useState(false);
  const [viewTicket,    setViewTicket]    = useState(null);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [printTicket,   setPrintTicket]   = useState(null);
  const [printTimestamp, setPrintTimestamp] = useState(null);
  const [previewTicket, setPreviewTicket] = useState(null); // Preview modal
  const [openMenu, setOpenMenu] = useState(null); // kebab menu ticket id
  const [menuCoords, setMenuCoords] = useState({ top: 0, left: 0 });

  // Parts list modal
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [partsModalIdx,  setPartsModalIdx]  = useState(0);
  const [allParts,       setAllParts]       = useState([]);
  const [partsSearch,    setPartsSearch]    = useState('');

  // Suggestions dropdown
  const [suggestions,  setSuggestions]  = useState([]);
  const [activeSugIdx, setActiveSugIdx] = useState(-1);

  const fetchTickets = useCallback(async () => {
    try {
      const params = {};
      if (search)   params.search = search;
      if (filterSt) params.status = filterSt;
      if (filterMode === 'month') { params.month = selectedMonth; params.year = selectedYear; }
      const { data } = await axios.get(`${API}/tickets`, { params });
      setTickets(data);
    } catch { showMsg('error', 'Σφάλμα φόρτωσης tickets'); }
    finally   { setLoading(false); }
  }, [search, filterSt, filterMode, selectedMonth, selectedYear]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Κλείσιμο kebab menu όταν κάνεις click αλλού
  useEffect(() => {
    function handleClickOutside() { setOpenMenu(null); }
    if (openMenu) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenu]);

  async function loadAllParts() {
    try { const { data } = await axios.get(`${API}/parts`); setAllParts(data); } catch {}
  }

  const stats = {
    total:       tickets.length,
    active:      tickets.filter(t => !['delivered','irreparable','credited'].includes(t.status)).length,
    delivered:   tickets.filter(t => t.status === 'delivered').length,
    credited:    tickets.filter(t => t.status === 'credited').length,
    ready:       tickets.filter(t => t.status === 'ready').length,
    waiting:     tickets.filter(t => t.status === 'waiting_parts').length,
    irreparable: tickets.filter(t => t.status === 'irreparable').length,
  };

  function showMsg(type, text) { setMsg({ type, text }); setTimeout(() => setMsg(null), 3500); }
  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function setPartField(idx, k, v) {
    setForm(f => {
      const items = [...f.partsItems];
      items[idx] = { ...items[idx], [k]: v };
      return { ...f, partsItems: items, total_price: calcTotal(f.labor_price, items) };
    });
  }

  function addPartRow() { setForm(f => ({ ...f, partsItems: [...f.partsItems, emptyPart()] })); }

  function removePartRow(idx) {
    setForm(f => {
      const items = f.partsItems.filter((_, i) => i !== idx);
      return { ...f, partsItems: items, total_price: calcTotal(f.labor_price, items) };
    });
  }

  function calcTotal(labor, items) {
    const l = parseFloat(labor) || 0;
    const p = items.reduce((sum, i) => sum + (parseFloat(i.parts_price) || 0), 0);
    return (l + p).toFixed(2);
  }

  function handleLaborChange(v) {
    setForm(f => ({ ...f, labor_price: v, total_price: calcTotal(v, f.partsItems) }));
  }

  async function lookupPart(idx) {
    const query = form.partsItems[idx].part_code.trim();
    if (!query) return;
    try {
      let data;
      try {
        const r = await axios.get(`${API}/parts/by-code/${query}`);
        data = r.data;
      } catch {
        const r = await axios.get(`${API}/parts/search/${encodeURIComponent(query)}`);
        if (!r.data.length) { showMsg('error', 'Δεν βρέθηκε ανταλλακτικό'); return; }
        data = r.data[0];
      }
      setForm(f => {
        const items = [...f.partsItems];
        items[idx] = { ...items[idx], part_code: data.code, part_name: data.name, parts_price: parseFloat(data.price_with_vat).toFixed(2) };
        return { ...f, partsItems: items, total_price: calcTotal(f.labor_price, items) };
      });
      setSuggestions([]);
    } catch { showMsg('error', 'Σφάλμα αναζήτησης ανταλλακτικού'); }
  }

  async function handlePartCodeChange(idx, v) {
    setPartField(idx, 'part_code', v);
    if (v.length < 2) { setSuggestions([]); return; }
    try {
      const { data } = await axios.get(`${API}/parts/search/${encodeURIComponent(v)}`);
      setSuggestions(data.slice(0, 6).map(p => ({ ...p, _idx: idx })));
      setActiveSugIdx(-1);
    } catch { setSuggestions([]); }
  }

  function selectSuggestion(p) {
    const idx = p._idx;
    setForm(f => {
      const items = [...f.partsItems];
      items[idx] = { ...items[idx], part_code: p.code, part_name: p.name, parts_price: parseFloat(p.price_with_vat).toFixed(2) };
      return { ...f, partsItems: items, total_price: calcTotal(f.labor_price, items) };
    });
    setSuggestions([]);
  }

  function openPartsModal(idx) {
    setPartsModalIdx(idx);
    setPartsSearch('');
    loadAllParts();
    setShowPartsModal(true);
  }

  function selectFromModal(p) {
    const idx = partsModalIdx;
    setForm(f => {
      const items = [...f.partsItems];
      items[idx] = { ...items[idx], part_code: p.code, part_name: p.name, parts_price: parseFloat(p.price_with_vat).toFixed(2) };
      return { ...f, partsItems: items, total_price: calcTotal(f.labor_price, items) };
    });
    setShowPartsModal(false);
  }

  const filteredModalParts = allParts.filter(p =>
    !partsSearch || p.code.toLowerCase().includes(partsSearch.toLowerCase()) || p.name.toLowerCase().includes(partsSearch.toLowerCase())
  ).sort((a, b) => {
    const q = partsSearch.toLowerCase();
    return (a.code.toLowerCase().startsWith(q) ? 0 : 1) - (b.code.toLowerCase().startsWith(q) ? 0 : 1);
  });

  function openNewModal()  { setForm({ ...EMPTY_FORM, partsItems: [emptyPart()] }); setViewTicket(null); setShowModal(true); }
  function closeModal()    { setShowModal(false); setViewTicket(null); setForm({ ...EMPTY_FORM, partsItems: [emptyPart()] }); setSuggestions([]); }

  function openEditModal(t) {
    setViewTicket(t);
    const partsItems = buildPartsItemsFromTicket(t);
    setForm({
      customer_name: t.customer_name || '', customer_phone: t.customer_phone || '',
      customer_email: t.customer_email || '', imei: t.imei || '',
      device_model: t.device_model || '', accessories: t.accessories || '',
      work_type: t.work_type || '', notes: t.notes || '',
      labor_price: t.labor_price || '', total_price: t.total_price || '',
      status: t.status || 'received',
      repair_notes: t.repair_notes || '',
      quantity_receipt: !!t.quantity_receipt,
      partsItems,
    });
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.customer_name.trim()) { showMsg('error', 'Το ονοματεπώνυμο πελάτη είναι υποχρεωτικό'); return; }
    const firstPart = form.partsItems[0];
    const extraParts = form.partsItems.slice(1).map(p => ({ part_code: p.part_code, part_name: p.part_name, parts_price: parseFloat(p.parts_price) || 0 }));
    const totalPartsPrice = form.partsItems.reduce((s, p) => s + (parseFloat(p.parts_price) || 0), 0);
    const payload = {
      customer_name: form.customer_name, customer_phone: form.customer_phone,
      customer_email: form.customer_email, imei: form.imei,
      device_model: form.device_model, accessories: form.accessories,
      work_type: form.work_type, notes: form.notes,
      part_code: firstPart.part_code || null, part_name: firstPart.part_name || null,
      extra_parts: extraParts.length ? extraParts : null,
      labor_price: parseFloat(form.labor_price) || 0,
      parts_price: totalPartsPrice,
      total_price: parseFloat(form.total_price) || 0,
      status: form.status,
      repair_notes: form.repair_notes || null,
      quantity_receipt: form.quantity_receipt ? 1 : 0,
    };
    try {
      if (viewTicket) {
        await axios.put(`${API}/tickets/${viewTicket.id}`, payload);
        showMsg('success', 'Το ticket ενημερώθηκε');
      } else {
        await axios.post(`${API}/tickets`, payload);
        showMsg('success', 'Νέο ticket δημιουργήθηκε');
      }
      closeModal(); fetchTickets();
    } catch (err) { showMsg('error', err.response?.data?.error || 'Σφάλμα αποθήκευσης'); }
  }

  async function changeStatus(ticketId, newStatus) {
    try { await axios.patch(`${API}/tickets/${ticketId}/status`, { status: newStatus }); fetchTickets(); }
    catch (err) { showMsg('error', err.response?.data?.error || 'Σφάλμα ενημέρωσης κατάστασης'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Διαγραφή αυτού του ticket;')) return;
    try { await axios.delete(`${API}/tickets/${id}`); showMsg('success', 'Διαγράφηκε'); fetchTickets(); }
    catch { showMsg('error', 'Σφάλμα διαγραφής'); }
  }

  async function completeTicket(t) {
    if (!window.confirm(`Ολοκλήρωση για "${t.customer_name}";\nΤα ανταλλακτικά θα αφαιρεθούν από την αποθήκη.`)) return;
    try {
      const { data } = await axios.post(`${API}/tickets/${t.id}/complete`);
      showMsg('success', data.message); fetchTickets();
    } catch (err) { showMsg('error', err.response?.data?.error || 'Σφάλμα ολοκλήρωσης'); }
  }

  async function markIrreparable(t) {
    if (!window.confirm(`Σήμανση ως "Ανεπισκεύαστο" για "${t.customer_name}";\nΔεν θα αφαιρεθεί κανένα ανταλλακτικό.`)) return;
    try {
      const { data } = await axios.post(`${API}/tickets/${t.id}/complete`, { irreparable: true });
      showMsg('success', data.message); fetchTickets();
    } catch (err) { showMsg('error', err.response?.data?.error || 'Σφάλμα'); }
  }

  async function reverseTicket(t) {
    if (!window.confirm(`Πιστωτικό για "${t.customer_name}";\nΘα επαναφερθούν τα ανταλλακτικά στο stock και το ticket θα αφαιρεθεί από τα έσοδα.`)) return;
    try {
      const { data } = await axios.post(`${API}/tickets/${t.id}/reverse`);
      showMsg('success', data.message); fetchTickets();
    } catch (err) { showMsg('error', err.response?.data?.error || 'Σφάλμα πιστωτικού'); }
  }

  function handlePrint(t) {
    const ts = new Date();
    const formatted = ts.toLocaleString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    setPrintTimestamp(formatted);
    setPrintTicket(t);
    setTimeout(() => { window.print(); setPrintTicket(null); setPrintTimestamp(null); }, 400);
  }

  function statusColor(s) {
    if (s === 'delivered')   return 'var(--green)';
    if (s === 'credited' || s === 'credited_deleted')    return 'var(--blue)';
    if (s === 'irreparable') return 'var(--danger)';
    if (s === 'ready')       return 'var(--accent)';
    return 'var(--text-muted)';
  }

  // ── Extra parts helper for preview
  function parseExtraParts(t) {
    if (!t.extra_parts) return [];
    try { return JSON.parse(t.extra_parts); } catch { return []; }
  }

  return (
    <div className="fade-in">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #print-area, #print-area * { visibility: visible !important; }
          @page { size: A4; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; }
          #print-area {
            position: absolute; top: 0; left: 0;
            width: 210mm;
            padding: 12mm 14mm;
            background: white; color: black;
            font-family: Arial, sans-serif;
            font-size: 11px;
            box-sizing: border-box;
            overflow: hidden;
          }
          /* Hide browser header/footer */
          #print-area::after { content: none !important; }
          #print-area .print-header { display: flex; flex-direction: column; align-items: center; justify-content: center; margin-bottom: 12px; text-align: center; }
          #print-area .print-logo-img { width: 80px; height: 80px; margin-bottom: 8px; object-fit: contain; }
          #print-area .print-logo-title { font-size: 18px; font-weight: bold; }
          #print-area .print-logo-sub { font-size: 10px; color: #777; }
          #print-area .print-ticket-badge { font-size: 11px; background: #f3f4f6; border: 1px solid #ddd; padding: 4px 10px; border-radius: 4px; }
          #print-area .print-section-title { font-size: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; color: #555; background: #f3f4f6; padding: 4px 8px; margin: 10px 0 6px; border-left: 3px solid #333; }
          #print-area .print-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 20px; margin-bottom: 4px; }
          #print-area .print-row { display: flex; gap: 8px; margin-bottom: 4px; border-bottom: 1px dotted #eee; padding-bottom: 3px; }
          #print-area .print-label { font-weight: bold; min-width: 140px; color: #555; font-size: 11px; flex-shrink: 0; }
          #print-area .print-val { font-size: 11px; color: #000; }
          #print-area .print-divider { border-top: 1px solid #ccc; margin: 8px 0; }
          #print-area .print-total-box { border: 2px solid #333; padding: 6px 12px; display: inline-block; font-size: 14px; font-weight: bold; margin-top: 4px; }
          #print-area .print-terms { margin-top: 10px; padding: 8px; border: 1px solid #ccc; border-radius: 4px; background: #fafafa; }
          #print-area .print-terms-title { font-size: 11px; font-weight: bold; margin-bottom: 5px; }
          #print-area .print-terms-item { font-size: 10px; color: #333; margin-bottom: 4px; padding-left: 10px; position: relative; line-height: 1.4; }
          #print-area .print-terms-item::before { content: '•'; position: absolute; left: 0; }
          #print-area .print-signature { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 16px; gap: 20px; }
          #print-area .print-sign-box { flex: 1; }
          #print-area .print-sign-line { border-bottom: 1px solid #333; margin-bottom: 4px; height: 32px; }
          #print-area .print-sign-label { font-size: 10px; color: #555; text-align: center; }
          #print-area .print-footer { font-size: 11px; color: #333; text-align: center; margin-top: 10px; border-top: 1px solid #eee; padding-top: 5px; font-weight: bold; }
        }
        .suggestion-item:hover { background: var(--surface) !important; }
        .preview-row { display: flex; padding: 8px 0; border-bottom: 1px solid var(--border); gap: 12px; }
        .preview-label { font-size: 12px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.4px; min-width: 160px; }
        .preview-val { font-size: 13px; color: var(--text); }
      `}</style>

      {/* Print area */}
      {printTicket && (
        <div id="print-area" ref={printRef}>

          {/* Header */}
          <div className="print-header">
            <img src={logo} alt="Mr. Gadget" className="print-logo-img" />
            <div>
              <div className="print-logo-title">THE Mr. GADGET</div>
              <div className="print-logo-sub">MOBILE ACCESSORIES & SERVICE</div>
            </div>
          </div>

          <div className="print-divider" />

          {/* Στοιχεία Πελάτη & Συσκευής — 2 columns */}
          <div className="print-section-title">Στοιχεία Πελάτη</div>
          <div className="print-grid">
            <div className="print-row"><span className="print-label">Ονοματεπώνυμο:</span><span className="print-val">{printTicket.customer_name || '—'}</span></div>
            <div className="print-row"><span className="print-label">Τηλέφωνο:</span><span className="print-val">{printTicket.customer_phone || '—'}</span></div>
            <div className="print-row"><span className="print-label">Email:</span><span className="print-val">{printTicket.customer_email || '—'}</span></div>
            <div className="print-row"><span className="print-label">IMEI:</span><span className="print-val">{printTicket.imei || '—'}</span></div>
          </div>

          <div className="print-section-title">Στοιχεία Συσκευής</div>
          <div className="print-grid">
            <div className="print-row"><span className="print-label">Συσκευή:</span><span className="print-val">{printTicket.device_model || '—'}</span></div>
            <div className="print-row"><span className="print-label">Παρελκόμενα:</span><span className="print-val">{printTicket.accessories || '—'}</span></div>
            <div className="print-row"><span className="print-label">Τύπος Εργασίας:</span><span className="print-val">{printTicket.work_type || '—'}</span></div>
            <div className="print-row"><span className="print-label">Πρόβλημα / Σημειώσεις:</span><span className="print-val">{printTicket.notes || '—'}</span></div>
          </div>

          {/* Ανταλλακτικά */}
          <div className="print-section-title">Ανταλλακτικά</div>
          <div className="print-row"><span className="print-label">Ανταλλακτικό 1:</span><span className="print-val">{printTicket.part_code ? `${printTicket.part_code} — ${printTicket.part_name}` : '—'}</span></div>
          {parseExtraParts(printTicket).map((p, i) => (
            <div key={i} className="print-row"><span className="print-label">Ανταλλακτικό {i+2}:</span><span className="print-val">{p.part_code} — {p.part_name}</span></div>
          ))}

{/* Κόστη - Εμφανίζονται ΜΟΝΟ αν υπάρχει κάποιο κόστος */}
          {(parseFloat(printTicket?.labor_price) > 0 || parseFloat(printTicket?.parts_price) > 0) && (
            <>
              <div className="print-section-title">Κόστος Επισκευής</div>
              <div className="print-grid">
                <div className="print-row"><span className="print-label">Κόστος Εργασίας:</span><span className="print-val">€{parseFloat(printTicket.labor_price||0).toFixed(2)}</span></div>
                <div className="print-row"><span className="print-label">Κόστος Ανταλλακτικών:</span><span className="print-val">€{parseFloat(printTicket.parts_price||0).toFixed(2)}</span></div>
              </div>
              <div className="print-total-box">ΣΥΝΟΛΟ: €{parseFloat(printTicket.total_price||0).toFixed(2)}</div>
            </>
          )}

          <div className="print-divider" style={{marginTop:10}} />

          {/* Όροι */}
          <div className="print-terms">
            <div className="print-terms-title">Όροι & Συγκατάθεση</div>
            <div className="print-terms-item">Υπογράφοντας αυτή την φόρμα εξουσιοδοτώ την VERSA MON ΙΚΕ να πραγματοποιήσει επισκευή στην συσκευή μου. Συμφωνώ πως η συσκευή μου παραδόθηκε με κάποιο πρόβλημα.</div>
            <div className="print-terms-item">Μετά την παρέλευση 60 ημερών από την ημερομηνία παραλαβής η συσκευή θεωρείται ότι απαξιώθηκε από τον πελάτη και θα καταστραφεί. Ελάχιστο κόστος ελέγχου 10,00€</div>
            <div className="print-terms-item">Καταλαβαίνω πως οποιαδήποτε εργασία γίνει πάνω στην συσκευή μου μπορεί να ακυρώσει την εγγύηση της με τον κατασκευαστή.</div>
            <div className="print-terms-item">Κατά την παράδοση της συσκευής σας, η εταιρεία μας δεν φέρει καμία ευθύνη για τυχόν απώλεια δεδομένων.</div>
            <div className="print-terms-item">Συγκατάθεση Επεξεργασίας Προσωπικών Δεδομένων: Συναινώ στη συλλογή και επεξεργασία των προσωπικών μου δεδομένων, αποκλειστικά για τους σκοπούς της επισκευής της συσκευής μου, σύμφωνα με τον Κανονισμό (ΕΕ) 2016/679.</div>
          </div>

          {/* Υπογραφές */}
          <div className="print-signature">
            <div className="print-sign-box">
              <div className="print-sign-line" />
              <div className="print-sign-label">Υπογραφή Πελάτη</div>
            </div>
            <div className="print-sign-box">
              <div className="print-sign-line" />
              <div className="print-sign-label">Υπογραφή Τεχνικού</div>
            </div>
          </div>

          {printTimestamp && (
            <div className="print-footer">Εκτυπώθηκε: {printTimestamp}</div>
          )}

          {/*<div className="print-footer">
            © {new Date().getFullYear()} Dimitris Gkountanos — Mr. Gadget Mobile Accessories & Service — Με επιφύλαξη παντός δικαιώματος
          </div>*/}
        </div>
      )}

      <div className="page-header">
        <div>
          <div className="page-title">Επισκευές</div>
          <div className="page-subtitle">Διαχείριση tickets επισκευής</div>
        </div>
        <button className="btn-primary" onClick={openNewModal}>+ Νέο Ticket</button>
      </div>

      {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

      <div className="stats-bar">
        <div className="stat-card"><div className="stat-label">Σύνολο</div><div className="stat-value blue">{stats.total}</div></div>
        <div className="stat-card"><div className="stat-label">Ενεργά</div><div className="stat-value yellow">{stats.active}</div></div>
        <div className="stat-card"><div className="stat-label">Έτοιμα</div><div className="stat-value green">{stats.ready}</div></div>
        <div className="stat-card"><div className="stat-label">Αναμονή</div><div className="stat-value purple">{stats.waiting}</div></div>
        <div className="stat-card"><div className="stat-label">Παραδ.</div><div className="stat-value" style={{color:'var(--green)'}}>{stats.delivered}</div></div>
        <div className="stat-card"><div className="stat-label">Πιστωτικά</div><div className="stat-value" style={{color:'var(--blue)'}}>{stats.credited}</div></div>
        <div className="stat-card"><div className="stat-label">Ανεπισκ.</div><div className="stat-value" style={{color:'var(--danger)'}}>{stats.irreparable}</div></div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div className="search-bar" style={{ maxWidth: 280 }}>
          <span className="search-icon"><SearchIcon /></span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Αναζήτηση πελάτη, IMEI, ticket..." />
        </div>
        <select value={filterSt} onChange={e => setFilterSt(e.target.value)} style={{ width: 'auto', minWidth: 160 }}>
          <option value="">Όλες οι καταστάσεις</option>
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterMode} onChange={e => setFilterMode(e.target.value)} style={{ width: 'auto', minWidth: 140 }}>
          <option value="month">Μήνας</option>
          <option value="all">Όλα</option>
        </select>
        {filterMode === 'month' && (
          <>
            <select value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value, 10))} style={{ width: 'auto' }}>
              {MONTH_NAMES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value, 10))} style={{ width: 'auto' }}>
              {Array.from({ length: 5 }).map((_, i) => {
                const y = now.getFullYear() - 2 + i; return <option key={y} value={y}>{y}</option>;
              })}
            </select>
          </>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state"><div className="empty-state-text">Φόρτωση...</div></div>
        ) : tickets.length === 0 ? (
          <div className="empty-state">
              <div className="empty-state-text">{search || filterSt ? 'Δεν βρέθηκαν αποτελέσματα' : 'Δεν υπάρχουν tickets ακόμα'}</div>
            </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Πελάτης</th>
                  <th>Τηλέφωνο</th>
                  <th>Συσκευή</th>
                  <th>Τύπος Εργασίας</th>
                  <th>Σύνολο</th>
                  <th>Κατάσταση</th>
                  <th>Τελ. Ενημέρωση</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(t => {
                  const isLocked = t.status === 'irreparable' || t.status === 'credited' || t.status === 'credited_deleted' || (t.status === 'delivered' && !isAdmin);
                  const canReverse = t.status === 'delivered' && (isAdmin || isTech);
                  const canDelete = isAdmin;
                  const canComplete = !['delivered', 'irreparable', 'credited'].includes(t.status);
                  return (
                    <tr key={t.id}>
                      <td><span className="mono" style={{ color: 'var(--accent)', fontSize: 11 }}>{t.ticket_number}</span></td>
                      <td style={{ fontWeight: 600 }}>{t.customer_name}</td>
                      <td style={{ color: 'var(--text-dim)' }}>{t.customer_phone || '—'}</td>
                      <td style={{ fontSize: 12 }}>{t.device_model || '—'}</td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.work_type || '—'}</td>
                      <td><span className="mono" style={{ color: 'var(--green)', fontWeight: 600 }}>€{parseFloat(t.total_price||0).toFixed(2)}</span></td>
                      <td>
                        {isLocked ? (
                          <span style={{
                            display: 'inline-block', padding: '4px 10px', borderRadius: 6,
                            background: 'var(--surface)', border: `1px solid ${statusColor(t.status)}`,
                            fontSize: 12, color: statusColor(t.status), fontWeight: 600
                          }}>
                            {STATUSES.find(s => s.value === t.status)?.label}
                          </span>
                        ) : (
                          <select value={t.status} onChange={e => changeStatus(t.id, e.target.value)}
                            style={{ width: 'auto', minWidth: 130, fontSize: 12, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '5px 8px' }}>
                            {STATUSES.filter(s => s.value !== 'credited_deleted').map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: 11 }}>{formatDate(t.updated_at || t.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {/* Κύρια ενέργεια: Ολοκλήρωση */}
                          {canComplete && (
                            <button className="btn-sm" onClick={() => completeTicket(t)}
                              style={{ background: 'var(--green)', color: '#fff', border: 'none', cursor: 'pointer', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              ✓ Ολοκλήρωση
                            </button>
                          )}
                          {/* Kebab menu ⋮ */}
                          <div style={{ position: 'relative', overflow: 'visible', zIndex: 2 }}>
                            <button
                              className="btn-ghost btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                setMenuCoords({
                                  top: rect.bottom + 4,
                                  left: Math.max(8, rect.right - 168),
                                });
                                setOpenMenu(openMenu === t.id ? null : t.id);
                              }}
                              style={{ padding: '5px 10px', fontSize: 16, lineHeight: 1 }}>
                              ⋮
                            </button>
                            {openMenu === t.id && createPortal(
                              <div
                                onClick={e => e.stopPropagation()}
                                style={{
                                  position: 'fixed',
                                  top: menuCoords.top,
                                  left: menuCoords.left,
                                  background: 'var(--surface)', border: '1px solid var(--border)',
                                  borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                                  zIndex: 200, minWidth: 160, overflow: 'hidden',
                                }}>
                                <MenuItem onClick={() => { setPreviewTicket(t); setOpenMenu(null); }}>Προβολή</MenuItem>
                                <MenuItem onClick={() => { handlePrint(t); setOpenMenu(null); }}>Εκτύπωση</MenuItem>
                                {(!isLocked || isAdmin) && <MenuItem onClick={() => { openEditModal(t); setOpenMenu(null); }}>Επεξεργασία</MenuItem>}
                                {canReverse && (
                                  <MenuItem danger onClick={() => { reverseTicket(t); setOpenMenu(null); }}>Πιστωτικό</MenuItem>
                                )}
                                {!['delivered','irreparable','credited','credited_deleted'].includes(t.status) && (
                                  <MenuItem danger onClick={() => { markIrreparable(t); setOpenMenu(null); }}>Ανεπισκεύαστο</MenuItem>
                                )}
                                {canDelete && <MenuItem danger onClick={() => { handleDelete(t.id); setOpenMenu(null); }}>Διαγραφή</MenuItem>}
                              </div>, document.body
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Ticket Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <div className="modal-title">
                {viewTicket
                  ? <>Ticket <span className="mono" style={{ fontSize: 13, color: 'var(--accent)' }}>{viewTicket.ticket_number}</span></>
                  : 'Νέο Ticket Επισκευής'}
              </div>
              <button className="close-btn" onClick={closeModal}><CloseIcon /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

                <div className="section-divider">Στοιχεία Πελάτη</div>
                <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
                  <div className="field field-required">
                    <label>Ονοματεπώνυμο</label>
                    <input value={form.customer_name} onChange={e => setField('customer_name', e.target.value)} placeholder="Όνομα Επώνυμο" />
                  </div>
                  <div className="field">
                    <label>Τηλέφωνο</label>
                    <input value={form.customer_phone} onChange={e => setField('customer_phone', e.target.value)} placeholder="69XXXXXXXX" />
                  </div>
                </div>
                <div className="form-grid form-grid-2" style={{ marginBottom: 14 }}>
                  <div className="field">
                    <label>Email</label>
                    <input type="email" value={form.customer_email} onChange={e => setField('customer_email', e.target.value)} placeholder="email@example.com" />
                  </div>
                  <div className="field">
                    <label>IMEI Συσκευής</label>
                    <input value={form.imei} onChange={e => setField('imei', e.target.value)} placeholder="15-ψήφιος αριθμός IMEI" className="mono" />
                  </div>
                </div>
                <div className="form-grid form-grid-2" style={{ marginBottom: 14 }}>
                  <div className="field">
                    <label>Καταχώρηση Συσκευής</label>
                    <input value={form.device_model} onChange={e => setField('device_model', e.target.value)} placeholder="π.χ. iPhone 14 Pro, Samsung S23" />
                  </div>
                  <div className="field">
                    <label>Παρελκόμενα</label>
                    <input value={form.accessories} onChange={e => setField('accessories', e.target.value)} placeholder="π.χ. Θήκη, Καλώδιο, Φορτιστής" />
                  </div>
                </div>

                <div className="section-divider">Σημειώσεις</div>
                <textarea rows={3} value={form.notes} onChange={e => setField('notes', e.target.value)}
                  placeholder="Περιγραφή προβλήματος, παρατηρήσεις..." style={{ resize: 'vertical', marginBottom: 14 }} />

                <div className="section-divider" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Ανταλλακτικά</span>
                  <button type="button" className="btn-ghost btn-sm" onClick={addPartRow} style={{ fontSize: 12, padding: '3px 10px' }}>+ Προσθήκη Ανταλλακτικού</button>
                </div>

                {form.partsItems.map((item, idx) => (
                  <div key={item.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '12px', marginBottom: 10, position: 'relative' }}>
                    {form.partsItems.length > 1 && (
                      <button type="button" onClick={() => removePartRow(idx)}
                        style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16 }}>✕</button>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, fontWeight: 600 }}>Ανταλλακτικό {idx + 1}</div>
                    <div className="form-grid form-grid-2" style={{ marginBottom: 10 }}>
                      <div className="field">
                        <label>Κωδικός / Ονομασία</label>
                        <div style={{ position: 'relative' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input value={item.part_code}
                              onChange={e => handlePartCodeChange(idx, e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') { e.preventDefault(); lookupPart(idx); }
                                if (e.key === 'Escape') setSuggestions([]);
                              }}
                              placeholder="Κωδικός ή ονομασία..." className="mono" style={{ flex: 1 }} />
                            <button type="button" className="btn-ghost btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => lookupPart(idx)}>Εύρεση</button>
                            <button type="button" className="btn-ghost btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => openPartsModal(idx)}>Λίστα</button>
                          </div>
                          {suggestions.length > 0 && suggestions[0]._idx === idx && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 100, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                              {suggestions.map((p, si) => (
                                <div key={p.id} className="suggestion-item" onMouseDown={() => selectSuggestion(p)}
                                  style={{ padding: '8px 12px', cursor: 'pointer', background: si === activeSugIdx ? 'var(--surface)' : 'transparent', borderBottom: '1px solid var(--border)' }}>
                                  <span className="mono" style={{ color: 'var(--accent)', fontSize: 11 }}>{p.code}</span>
                                  <span style={{ marginLeft: 8, fontSize: 12 }}>{p.name}</span>
                                  <span style={{ float: 'right', fontSize: 11, color: 'var(--green)' }}>€{parseFloat(p.price_with_vat).toFixed(2)}</span>
                                  <span style={{ float: 'right', marginRight: 12, fontSize: 11, color: p.quantity === 0 ? 'var(--danger)' : 'var(--text-muted)' }}>Qty: {p.quantity}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="field">
                        <label>Ονομασία</label>
                        <input value={item.part_name} onChange={e => setPartField(idx, 'part_name', e.target.value)} placeholder="Συμπληρώνεται αυτόματα" />
                      </div>
                    </div>
                    <div className="field" style={{ maxWidth: 160 }}>
                      <label>Τιμή (€)</label>
                      <input type="number" min="0" step="0.01" value={item.parts_price} /*onChange={e => setPartField(idx, 'parts_price', e.target.value)}για να μην αλλαζει την τιμη*/ placeholder="0.00" />
                    </div>
                  </div>
                ))}

              <div className="section-divider">Τιμολόγηση Επισκευής</div>
                <div className="price-group" style={{ gridTemplateColumns: '1fr 1fr', marginBottom: 14 }}>
                  <div>
                    <div className="price-label">Τιμή Εργασίας (€)</div>
                    <input 
                      type="number" 
                      min="0" 
                      step="0.01" 
                      value={form.labor_price} 
                      onChange={e => handleLaborChange(e.target.value)} 
                      placeholder="0.00" 
                    />
                  </div>
                  <div>
                    <div className="price-label" style={{ color: 'var(--green)' }}>Σύνολο (€)</div>
                    <input 
                      type="number" 
                      min="0" 
                      step="0.01" 
                      value={form.total_price} 
                      onChange={e => {
                        const val = e.target.value;
                        const newTotal = parseFloat(val) || 0;
                        // Υπολογίζουμε πόσο κάνουν τα ανταλλακτικά
                        const partsTotal = form.partsItems.reduce((s, p) => s + (parseFloat(p.parts_price) || 0), 0);
                        // Η διαφορά πάει αυτόματα στην εργασία
                        const diff = (newTotal - partsTotal).toFixed(2);
                        
                        setForm(f => ({ 
                          ...f, 
                          total_price: val, 
                          labor_price: diff >= 0 ? diff : 0 // Αν το σύνολο είναι μικρότερο, η εργασία πάει στο 0
                        }));
                      }} 
                      placeholder="0.00" 
                      style={{ color: 'var(--green)', fontWeight: 600 }} 
                    />
                  </div>
                </div>

                {/* Σχόλια Επισκευής — εσωτερικά, δεν εκτυπώνονται */}
                <div className="section-divider" style={{ color: 'var(--yellow)' }}>Σχόλια Επισκευής (Εσωτερικά)</div>
                <textarea
                  rows={3}
                  value={form.repair_notes}
                  onChange={e => setField('repair_notes', e.target.value)}
                  placeholder="Εσωτερικά σχόλια τεχνικού — δεν εμφανίζονται στην εκτύπωση..."
                  style={{ resize: 'vertical', marginBottom: 14, borderColor: 'var(--yellow)', opacity: 0.9 }}
                />

                <div className="section-divider">Κατάσταση Επισκευής</div>
                <div className="form-grid form-grid-2" style={{ marginBottom: 16 }}>
                  <div className="field">
                    <label>Τύπος Εργασίας</label>
                    <select value={form.work_type} onChange={e => setField('work_type', e.target.value)}>
                      {WORK_TYPES.map(w => <option key={w} value={w}>{w || '— Επιλέξτε τύπο —'}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label>Τρέχουσα Κατάσταση</label>
                    <select value={form.status} onChange={e => setField('status', e.target.value)}
                      disabled={!isAdmin && ['delivered','irreparable'].includes(viewTicket?.status)}
                      style={{ opacity: (!isAdmin && ['delivered','irreparable'].includes(viewTicket?.status)) ? 0.5 : 1 }}>
                      {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="status-tracker">
                  {STATUSES.filter(s => s.value !== 'irreparable').map((s, i) => {
                    const cur = STATUS_IDX[form.status] ?? 0;
                    const cls = i < cur ? 'done' : i === cur ? 'current' : '';
                    return (
                      <div className="status-step" key={s.value}>
                        <div className={`step-dot ${cls}`}>{i < cur && <CheckIcon />}</div>
                        <div className="step-label">{s.short}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-ghost" onClick={closeModal}>Ακύρωση</button>
                <button type="submit" className="btn-primary">{viewTicket ? 'Ενημέρωση Ticket' : 'Δημιουργία Ticket'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Preview Modal (read-only) ── */}
      {previewTicket && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPreviewTicket(null)}>
          <div className="modal" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <div className="modal-title">
                Ticket <span className="mono" style={{ fontSize: 13, color: 'var(--accent)' }}>{previewTicket.ticket_number}</span>
                <span style={{ marginLeft: 12, fontSize: 12, padding: '2px 10px', borderRadius: 6, background: 'var(--surface)', border: `1px solid ${statusColor(previewTicket.status)}`, color: statusColor(previewTicket.status), fontWeight: 600 }}>
                  {STATUSES.find(s => s.value === previewTicket.status)?.label}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-ghost btn-sm" onClick={() => handlePrint(previewTicket)}>Εκτύπωση</button>
                <button className="close-btn" onClick={() => setPreviewTicket(null)}><CloseIcon /></button>
              </div>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

              <div className="section-divider">Στοιχεία Πελάτη</div>
              <div className="preview-row"><span className="preview-label">Ονοματεπώνυμο</span><span className="preview-val">{previewTicket.customer_name || '—'}</span></div>
              <div className="preview-row"><span className="preview-label">Τηλέφωνο</span><span className="preview-val">{previewTicket.customer_phone || '—'}</span></div>
              <div className="preview-row"><span className="preview-label">Email</span><span className="preview-val">{previewTicket.customer_email || '—'}</span></div>
              <div className="preview-row"><span className="preview-label">IMEI</span><span className="preview-val mono">{previewTicket.imei || '—'}</span></div>
              <div className="preview-row"><span className="preview-label">Συσκευή</span><span className="preview-val">{previewTicket.device_model || '—'}</span></div>
              <div className="preview-row" style={{ borderBottom: 'none' }}><span className="preview-label">Παρελκόμενα</span><span className="preview-val">{previewTicket.accessories || '—'}</span></div>

              <div className="section-divider">Εργασία</div>
              <div className="preview-row"><span className="preview-label">Τύπος Εργασίας</span><span className="preview-val">{previewTicket.work_type || '—'}</span></div>
              <div className="preview-row"><span className="preview-label">Σημειώσεις</span><span className="preview-val">{previewTicket.notes || '—'}</span></div>
              {previewTicket.repair_notes && (
                <div className="preview-row" style={{ borderBottom: 'none', background: 'rgba(245,158,11,0.07)', borderRadius: 6, padding: '8px 12px' }}>
                  <span className="preview-label" style={{ color: 'var(--yellow)' }}>Σχόλια Τεχνικού</span>
                  <span className="preview-val">{previewTicket.repair_notes}</span>
                </div>
              )}

              <div className="section-divider">Ανταλλακτικά</div>
              {previewTicket.part_code ? (
                <div className="preview-row">
                  <span className="preview-label">Ανταλλακτικό 1</span>
                  <span className="preview-val"><span className="mono" style={{ color: 'var(--accent)', fontSize: 12 }}>{previewTicket.part_code}</span> — {previewTicket.part_name}</span>
                </div>
              ) : (
                <div className="preview-row"><span className="preview-label">Ανταλλακτικό</span><span className="preview-val" style={{ color: 'var(--text-muted)' }}>—</span></div>
              )}
              {parseExtraParts(previewTicket).map((p, i) => (
                <div key={i} className="preview-row">
                  <span className="preview-label">Ανταλλακτικό {i + 2}</span>
                  <span className="preview-val"><span className="mono" style={{ color: 'var(--accent)', fontSize: 12 }}>{p.part_code}</span> — {p.part_name}</span>
                </div>
              ))}

              <div className="section-divider">Τιμολόγηση</div>
              <div className="preview-row"><span className="preview-label">Κόστος Εργασίας</span><span className="preview-val mono">€{parseFloat(previewTicket.labor_price||0).toFixed(2)}</span></div>
              <div className="preview-row"><span className="preview-label">Κόστος Ανταλλακτικών</span><span className="preview-val mono">€{parseFloat(previewTicket.parts_price||0).toFixed(2)}</span></div>
              <div className="preview-row" style={{ borderBottom: 'none' }}>
                <span className="preview-label">Σύνολο</span>
                <span className="preview-val mono" style={{ color: 'var(--green)', fontWeight: 700, fontSize: 16 }}>€{parseFloat(previewTicket.total_price||0).toFixed(2)}</span>
              </div>

              <div className="section-divider">Πληροφορίες</div>
              <div className="preview-row"><span className="preview-label">Δημιουργία</span><span className="preview-val">{formatDate(previewTicket.created_at)}</span></div>
              <div className="preview-row" style={{ borderBottom: 'none' }}><span className="preview-label">Τελ. Ενημέρωση</span><span className="preview-val">{formatDate(previewTicket.updated_at || previewTicket.created_at)}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ── Parts List Modal ── */}
      {showPartsModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowPartsModal(false)}>
          <div className="modal" style={{ maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header">
              <div className="modal-title">Λίστα Αποθήκης</div>
              <button className="close-btn" onClick={() => setShowPartsModal(false)}><CloseIcon /></button>
            </div>
            <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
              <div className="search-bar" style={{ maxWidth: '100%' }}>
                <span className="search-icon"><SearchIcon /></span>
                <input value={partsSearch} onChange={e => setPartsSearch(e.target.value)} placeholder="Αναζήτηση κωδικού ή ονομασίας..." autoFocus />
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
              {filteredModalParts.length === 0 ? (
                <div className="empty-state"><div className="empty-state-text">Δεν βρέθηκαν αποτελέσματα</div></div>
              ) : (
                filteredModalParts.map(p => (
                  <div key={p.id} onClick={() => selectFromModal(p)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 24px', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div>
                      <span className="mono" style={{ color: 'var(--accent)', fontSize: 12 }}>{p.code}</span>
                      <span style={{ marginLeft: 12, fontSize: 13 }}>{p.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: p.quantity === 0 ? 'var(--danger)' : p.quantity <= 2 ? '#f59e0b' : 'var(--green)', fontWeight: 700 }}>Qty: {p.quantity}</span>
                      <span className="mono" style={{ color: 'var(--green)', fontWeight: 600 }}>€{parseFloat(p.price_with_vat).toFixed(2)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({ children, onClick, danger }) {
  return (
    <div onClick={onClick}
      style={{
        padding: '9px 16px', cursor: 'pointer', fontSize: 13,
        color: danger ? 'var(--danger)' : 'var(--text)',
        borderBottom: '1px solid var(--border)',
        transition: 'background .15s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      {children}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function SearchIcon() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>;
}
function CloseIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>;
}
function CheckIcon() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>;
}